/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * InquiryInfo common Page component
 *
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.15.0
 */

import { useCallback } from 'react';

import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@components/common';
import { formatNumberToPhone } from '@components/common/PhoneMask/formatNumberToPhone';
import { getTimeZone } from '@components/Table/helpers/getTimeZone';
import type { ICommonTranslation } from '@modules/internationalization/types';
import type { IPageInquiryInfoTranslation } from '@modules/internationalization/types/pages/inquiryInfo';
import { modalOpen } from '@modules/modal/modalActions';
import type { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { formatInquiryNumber, getPrintableText } from '@utils/formatText';
import { stringFormatParams } from '@utils/stringFormat';

import { makeSelectIsFeatureWithVersionEnabled } from '../../PageAuth/selectors/authSelectors';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import {
    inquiryInfoApiCreateDateSelector,
    inquiryInfoApiDescriptionSelector,
    inquiryInfoApiIsInquiryCanBeClosedSelector,
    inquiryInfoApiIsShowCloseInquiryButtonSelector,
    inquiryInfoApiCustomerNameSelector,
    inquiryInfoApiStateIdSelector,
    inquiryInfoApiStateNameSelector,
    inquiryInfoApiSubscriberMsisdnSelector,
    inquiryInfoApiTopicDefSelector,
    inquiryInfoApiServiceTypeSelector,
    inquiryInfoApiLastActionDateSelector,
    inquiryInfoApiNotifyPhoneSelector,
    inquiryInfoApiNotifyMailSelector, inquiryInfoApiVisibleInquiryAttributesSelector,
} from '../selectors';
import { EDataType, HIDDEN_VISIBILITY, IInquiryAttributeResponseData } from '../types/inquiryInfoType';
import InquiryCloseModal from './closeInquiry/InquiryCloseModal';
import {
    InquiryInfoPageContentStyled,
    InquiryInfoPageStyled,
    InquiryInfoPageTitleStyled,
    InquiryInfoPageStatusStyled,
} from './styled/InquiryInfoPageStyled';

export interface IInquiryInfoPageCommonInfo {
    inquiryId: string;
    translationCommon: ICommonTranslation;
    translationInquiryInfo: IPageInquiryInfoTranslation;
}

const InquiryInfoPageCommonInfo = (pageProps: IInquiryInfoPageCommonInfo) => {
    const {
        inquiryId,
        translationInquiryInfo,
        translationCommon,
    } = pageProps;

    const dispatch = useDispatch();

    const isInquiriesV3Enabled = useSelector(makeSelectIsFeatureWithVersionEnabled(
        FeatureNameEnum.INQUIRIES, 3));

    const createDate = useSelector(inquiryInfoApiCreateDateSelector(inquiryId));
    const description = useSelector(inquiryInfoApiDescriptionSelector(inquiryId));
    const topicDef = useSelector(inquiryInfoApiTopicDefSelector(inquiryId));
    const stateId = useSelector(inquiryInfoApiStateIdSelector(inquiryId));
    const stateName = useSelector(inquiryInfoApiStateNameSelector(inquiryId));
    const msisdn = useSelector(inquiryInfoApiSubscriberMsisdnSelector(inquiryId));
    const customer = useSelector(inquiryInfoApiCustomerNameSelector(inquiryId));
    const serviceType = useSelector(inquiryInfoApiServiceTypeSelector(inquiryId));
    const lastActionDate = useSelector(inquiryInfoApiLastActionDateSelector(inquiryId));
    const notifyPhone = useSelector(inquiryInfoApiNotifyPhoneSelector(inquiryId));
    const notifyMail = useSelector(inquiryInfoApiNotifyMailSelector(inquiryId));
    const isShowCloseInquiryButton = useSelector(inquiryInfoApiIsShowCloseInquiryButtonSelector(inquiryId));
    const isInquiryCanBeClosed = useSelector(inquiryInfoApiIsInquiryCanBeClosedSelector(inquiryId));
    const visibleInquiryAttributes = useSelector(inquiryInfoApiVisibleInquiryAttributesSelector(inquiryId));

    const openCloseInquiryModal = useCallback(() => {
        dispatch(modalOpen({
            render: (props: IModalRenderProps) => <InquiryCloseModal
                inquiryId={inquiryId}
                translation={translationInquiryInfo}
                translationCommon={translationCommon}
                onCloseModal={props.onCloseModal}
            />,
        }));
    }, [inquiryId, translationCommon, translationInquiryInfo]);

    const getInquiryOption = useCallback((item: IInquiryAttributeResponseData) => {
        switch (item.declaration.dataType) {
            case EDataType.BOOL:
                return item.valueList[0]?.value === '1'
                    ? translationInquiryInfo.labelDefined
                    : translationInquiryInfo.labelUndefined;
            case EDataType.DATE:
                return item.valueList?.length > 0
                    ? moment(item.valueList[0].value).format(EFormatData.FullDateWithTime) || ' '
                    || getTimeZone(item.valueList[0].value)
                    : translationInquiryInfo.labelUndefined
            case EDataType.DICTIONARY:
                return item.valueList?.length > 0
                    ? item.valueList.map(i => i.value.toString()).join(', ')
                    : translationInquiryInfo.labelUndefined
            default:
                return item.valueList?.length > 0
                    ? item.valueList[0].value
                    : translationInquiryInfo.labelUndefined;
        }
    }, [translationInquiryInfo]);

    return (
        <InquiryInfoPageStyled>
            {/*Заголовок + кнопка "Закрыть обращение"*/}
            <InquiryInfoPageTitleStyled>
                <div className="inquiry-info-page__title">
                    {
                        stringFormatParams(translationInquiryInfo.title, {
                            number: formatInquiryNumber(inquiryId),
                            date: moment(createDate).format(EFormatData.FullData),
                        })
                    }
                </div>
                {
                    isShowCloseInquiryButton &&
                    <Button
                        className="inquiry-info-page__button"
                        disabled={!isInquiryCanBeClosed}
                        kind="mediumNormal"
                        onClick={openCloseInquiryModal}
                    >
                        {translationInquiryInfo.closeModal.btn}
                    </Button>
                }
            </InquiryInfoPageTitleStyled>
            {/*Тема обращения*/}
            <div className="inquiry-info-page__subtitle">
                {translationInquiryInfo.inquiryTopic}
            </div>
            <div className="inquiry-info-page__text">
                {topicDef}
            </div>
            {/*Описание / Параметры обращения*/}
            <div className="inquiry-info-page__subtitle">
                {
                    isInquiriesV3Enabled
                        ? translationInquiryInfo.inquiryOptions
                        : translationInquiryInfo.description
                }
            </div>
            <div className="inquiry-info-page__text">
                {
                    isInquiriesV3Enabled
                        ?
                        <div>
                            {
                                visibleInquiryAttributes.length > 0 &&
                                visibleInquiryAttributes.map((item) => {
                                    if (HIDDEN_VISIBILITY !== item.declaration.visibility) {
                                        return <div dangerouslySetInnerHTML={({
                                            __html: item.name.concat(': ', getPrintableText(getInquiryOption(item))),
                                        })} />
                                    }
                                })
                            }
                            {
                                description &&
                                <div dangerouslySetInnerHTML={({
                                    __html:
                                        translationInquiryInfo.description.concat(': ', getPrintableText(description)),
                                })} />
                            }
                        </div>
                        : <div dangerouslySetInnerHTML={({
                            __html: description
                                ? getPrintableText(description)
                                : translationInquiryInfo.emptyDescription,
                        })} />
                }
            </div>
            {/*Основные атрибуты должны быть в две колонки*/}
            <InquiryInfoPageContentStyled>
                <div>
                    {/*Статус*/}
                    <div className="inquiry-info-page__subtitle">
                        {translationInquiryInfo.status}
                    </div>
                    <InquiryInfoPageStatusStyled statusId={stateId}>
                        {stateName}
                    </InquiryInfoPageStatusStyled>
                    {/*Создано*/}
                    <div className="inquiry-info-page__subtitle">
                        {translationInquiryInfo.creationInfo}
                    </div>
                    <div className="inquiry-info-page__text">
                        {msisdn && formatNumberToPhone(msisdn) + ' / '}
                        {customer}
                    </div>
                    {/*Тип услуг*/}
                    {
                        serviceType &&
                        <div>
                            <div className="inquiry-info-page__subtitle">
                                {translationInquiryInfo.serviceTypeTitle}
                            </div>
                            <div className="inquiry-info-page__text">
                                {translationInquiryInfo.serviceType[serviceType]}
                            </div>
                        </div>
                    }
                    {/*Дата обновления*/}
                    <div className="inquiry-info-page__subtitle">
                        {translationInquiryInfo.lastUpdateDate}
                    </div>
                    <div className="inquiry-info-page__text">
                        {moment(lastActionDate).format(EFormatData.FullDateWithTime)}&nbsp;
                        {lastActionDate && getTimeZone(lastActionDate)}
                    </div>
                </div>
                <div>
                    {/*Телефон*/}
                    {
                        notifyPhone && <div>
                            <div className="inquiry-info-page__subtitle">
                                {translationInquiryInfo.phoneNumber}
                            </div>
                            <div className="inquiry-info-page__text">
                                {notifyPhone}
                            </div>
                        </div>
                    }
                    {/*Почта*/}
                    {
                        notifyMail && <div>
                            <div className="inquiry-info-page__subtitle">
                                {translationInquiryInfo.email}
                            </div>
                            <div className="inquiry-info-page__text">
                                {notifyMail}
                            </div>
                        </div>
                    }
                </div>
            </InquiryInfoPageContentStyled>
        </InquiryInfoPageStyled>
    );
};

export default InquiryInfoPageCommonInfo;
